import React, { useState, useEffect, useRef } from "react";
import { FONT_SIZES } from "../../constants/fontSizes";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Box,
} from "@mui/material";
import {
  styles,
  columnDescriptions,
  isNumericColumn,
} from "./DataTable.styles";

// Helper for rendering a value in a filled circle
const NumericCircle = ({ value, backgroundColor }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: "50%",
      background: backgroundColor || "#888",
      color: "#fff",
      fontWeight: 700,
      fontSize: 18,
      margin: "0 auto",
    }}
  >
    {value}
  </span>
);

const DataTable = ({ columns, data, isMobile, flipColumns }) => {
  const containerRef = useRef(null);
  const [clickedCell, setClickedCell] = useState(null);

  // Ensure data is an array
  const safeData = Array.isArray(data) ? data : [];

  const isNameColumn = (columnId) => {
    return columnId === "name" || columnId === "Name Candidate";
  };

  const handleCellClick = (rowKey, columnId) => {
    if (isNameColumn(columnId)) {
      setClickedCell(
        clickedCell === `${rowKey}-${columnId}` ? null : `${rowKey}-${columnId}`
      );
    }
  };

  return (
    <Box ref={containerRef}>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table
          sx={{
            ...styles.table,
            borderCollapse: "separate",
            borderSpacing: "20px 20px",
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    width: column.width,
                    border: "none",
                    textAlign: "center",
                    ...styles.headerCell,
                    ...(flipColumns &&
                      column.id !== "select" &&
                      styles.mobileHeaderCell),
                    ...(column.headerStyle || {}),
                    ...(isMobile && {
                      fontSize: FONT_SIZES.TABLE_COLUMN.xs,
                    }),
                    ...(isNameColumn(column.id) && {
                      fontSize: FONT_SIZES.TABLE_COLUMN,
                    }),
                  }}
                >
                  <Tooltip
                    title={
                      column.tooltip ||
                      columnDescriptions[column.id] ||
                      columnDescriptions[column.label] ||
                      ""
                    }
                    placement="top"
                    arrow
                    componentsProps={column.tooltipProps}
                  >
                    <span style={styles.columnHeader}>
                      {column.headerFormat
                        ? column.headerFormat()
                        : typeof column.label === "string"
                        ? column.label.trim()
                        : column.label}
                    </span>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {safeData.map((row, index) => {
              const rowKey = row.name || row["Name Candidate"] || index;

              return (
                <TableRow key={`row-${rowKey}-${index}`}>
                  {columns.map((column) => {
                    const value = row[column.id];
                    const formattedValue = column.format
                      ? column.format(value, row)
                      : value;
                    const backgroundColor = column.getColor
                      ? column.getColor(value)
                      : undefined;
                    const cellKey = `${rowKey}-${column.id}`;
                    const showTooltip =
                      isNameColumn(column.id) && clickedCell === cellKey;

                    return (
                      <Tooltip
                        key={`tooltip-${cellKey}`}
                        title={isNameColumn(column.id) ? value : ""}
                        open={showTooltip}
                        placement="top"
                        arrow
                      >
                        <TableCell
                          key={`cell-${cellKey}`}
                          onClick={() => handleCellClick(rowKey, column.id)}
                          sx={{
                            backgroundColor,
                            width: column.width,
                            textAlign: "center",
                            whiteSpace:
                              column.id === "statement_texts"
                                ? "nowrap"
                                : "normal",
                            cursor: isNameColumn(column.id)
                              ? "pointer"
                              : "default",
                            ...styles.cell,
                            ...(isMobile && styles.mobileCell),
                            ...(isNameColumn(column.id) && {
                              fontSize: FONT_SIZES.TABLE_CELL,
                            }),
                            ...(isNumericColumn(column.id) && {
                              // color: "#fff",
                              padding: "5px 0",
                              borderRadius: "8px",
                              // background: backgroundColor || "#888",
                              margin: "0 4px",
                            }),
                          }}
                        >
                          {isNumericColumn(column.id) ? (
                            <NumericCircle
                              value={formattedValue}
                              backgroundColor={backgroundColor}
                            />
                          ) : (
                            formattedValue
                          )}
                        </TableCell>
                      </Tooltip>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DataTable;
